/**
 * A new WB Packing Slip. Nothing is written, and no number spent, until the first Post. With
 * `?mtnLine=<id>` it is raised from a Material Transfer Note row (docs/designs/mtn-seed-indent.md,
 * Phase 2): the lot, header and pick come from the note.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { FormPageSkeleton } from "@/components/app/skeletons";
import { Callout } from "@/components/ui/callout";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { errMsg, useMtnLineForWb } from "@/features/mtn/hooks";
import { WORK_ORDER_FORM } from "@/features/packaging/api";
import { WorkOrderPage } from "@/features/packaging/components/work-order-page";

export const Route = createFileRoute("/_app/packaging/wb-packing-slip/new")({
  validateSearch: z.object({
    mtnLine: z.number().int().positive().optional(),
  }),
  beforeLoad: () => {
    if (!useAuth.getState().canCreateForm(WORK_ORDER_FORM))
      throw redirect({ to: "/packaging/wb-packing-slip" });
  },
  component: Page,
});

function Page() {
  const { mtnLine } = Route.useSearch();
  const { selected, facilities, ready } = useFacilities();
  const mtn = useMtnLineForWb(mtnLine);
  if (!ready || (mtnLine !== undefined && mtn.isPending))
    return <FormPageSkeleton />;
  if (mtnLine !== undefined && mtn.isError)
    return (
      <Callout
        tone="danger"
        title="Could not load the Material Transfer Note row"
      >
        {errMsg(mtn.error)}
      </Callout>
    );
  // A slip raised from a note is at the note's plant.
  const plant = mtn.data
    ? facilities.find((f) => f.id === mtn.data.facilityId)
    : selected;
  return (
    <WorkOrderPage
      order={null}
      facilityId={plant?.id}
      plantName={plant ? `${plant.name} (${plant.plantCode})` : null}
      fromMtn={mtn.data}
    />
  );
}
